import { useState, useMemo } from 'react';
import {
  Button,
  Badge,
  Checkbox,
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@aaas/shared';
import { Filter, X, Check } from 'lucide-react';

interface AccessMatrixSpace {
  id: string;
  name: string;
}

interface SpaceSearchFilterProps {
  spaces: AccessMatrixSpace[];
  selectedSpaceIds: string[];
  onSelectionChange: (spaceIds: string[]) => void;
}

export function SpaceSearchFilter({
  spaces,
  selectedSpaceIds,
  onSelectionChange,
}: SpaceSearchFilterProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter and limit to 10
  const filteredSpaces = useMemo(() => {
    if (!searchQuery) return spaces.slice(0, 10);
    const query = searchQuery.toLowerCase();
    return spaces.filter((p) => p.name.toLowerCase().includes(query)).slice(0, 10);
  }, [spaces, searchQuery]);

  const toggleSpace = (spaceId: string) => {
    if (selectedSpaceIds.includes(spaceId)) {
      onSelectionChange(selectedSpaceIds.filter((id) => id !== spaceId));
    } else {
      onSelectionChange([...selectedSpaceIds, spaceId]);
    }
  };

  const selectedSpaces = spaces.filter((p) => selectedSpaceIds.includes(p.id));

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start min-h-10 h-auto py-2">
              {selectedSpaceIds.length === 0 ? (
                <>
                  <Filter className="mr-2 h-4 w-4 flex-shrink-0" />
                  Select spaces to display...
                </>
              ) : (
                <div className="flex items-center gap-2 flex-wrap w-full">
                  <Filter className="h-4 w-4 flex-shrink-0" />
                  {selectedSpaces.map((space) => (
                    <Badge key={space.id} variant="secondary" className="gap-1 pr-1">
                      <span>{space.name}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-4 w-4 p-0 hover:bg-transparent"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSpace(space.id);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ))}
                </div>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="p-0"
            align="start"
            style={{ width: 'var(--radix-popover-trigger-width)' }}
          >
            <Command>
              <CommandInput
                placeholder="Search spaces..."
                value={searchQuery}
                onValueChange={setSearchQuery}
              />
              <CommandList>
                <CommandEmpty>No spaces found.</CommandEmpty>
                <CommandGroup
                  heading={`Spaces${filteredSpaces.length === 10 ? ' (showing first 10)' : ''}`}
                >
                  {filteredSpaces.map((space) => {
                    const isSelected = selectedSpaceIds.includes(space.id);
                    return (
                      <CommandItem key={space.id} onSelect={() => toggleSpace(space.id)}>
                        <Checkbox
                          checked={isSelected}
                          className="mr-2"
                          onCheckedChange={() => toggleSpace(space.id)}
                        />
                        <span className="flex-1">{space.name}</span>
                        {isSelected && <Check className="ml-auto h-4 w-4" />}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {selectedSpaceIds.length > 0 && (
          <Button variant="ghost" size="sm" onClick={() => onSelectionChange([])}>
            Clear All
          </Button>
        )}
      </div>
    </div>
  );
}
